/**
 * Sheet 07c — log in (A.5, SEC.7; FR-020, FR-021; callouts 6, 7, 10).
 *
 * One form for every role. Every refusal shows the same sentence: wrong
 * password, unknown address and lockout are one `invalid-credentials` body
 * with identical bytes (SEC.7, NFR-003), and the copy must not undo that. The
 * 503 `try-again` (Argon2id semaphore) gets its own wording. On success `next`
 * is carried to /mfa in router state, and every cached query is dropped first
 * so the /mfa gate refetches instead of acting on the stale 401, and no other
 * tenant's cached data survives into this sign-in (TEN.1; mission 6 review
 * follow-up 14, cycle 2 finding 1). A
 * reporter sent here by the session-expiry handler (`lib/session.ts`; mission 6
 * review follow-up 14, A-1) is told the session ended, from router state only,
 * and the path it left is carried on to /mfa as `returnTo`, again in router
 * state only; /mfa decides whether it is safe to land on (FR-026; U26).
 *
 * Mission 10 WP18 T109 (FR-013's screen half, FR-019; U27): the account-deletion request signs the reporter
 * out to `/login?notice=account-closed`, and the screen renders register 37 `deletionSignedOutNotice` above
 * the form. That is the one `notice` value honoured; any other is ignored.
 */
import { useId, useState, type FormEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { PROBLEM, isProblem, login } from '@/lib/auth'
import { copyText } from '@/lib/provisional-copy'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { clearSessionCache } from '@/components/trellis/AuthGate'
import { AuthLayout, DisabledReason, failureCopy } from './AuthLayout'

/** The one failure sentence (SEC.7). The MFA challenge reuses it. */
export const LOGIN_FAILED_MESSAGE = 'That email or password is not right.'

function arrivedExpired(state: unknown): state is { sessionExpired: true; from?: unknown } {
  return typeof state === 'object' && state !== null && (state as { sessionExpired?: unknown }).sessionExpired === true
}

export default function Login() {
  const navigate = useNavigate()
  const arrival: unknown = useLocation().state
  const expired = arrivedExpired(arrival)
  // Only a session expiry carries a return path (FR-026). Mfa validates it.
  const returnTo = expired ? arrival.from : undefined
  const [params] = useSearchParams()
  const accountClosed = params.get('notice') === 'account-closed'
  const queryClient = useQueryClient()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const emailId = useId()
  const passwordId = useId()
  const reasonId = useId()

  const reason =
    email.trim() === '' || password === ''
      ? 'Log in is unavailable until you enter your email and password.'
      : null
  const canSubmit = reason === null && !submitting

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    setSubmitting(true)
    setError(null)
    try {
      const res = await login({ email: email.trim(), password })
      clearSessionCache(queryClient)
      navigate('/mfa', { state: { next: res.next, returnTo } })
    } catch (err) {
      setError(isProblem(err, PROBLEM.INVALID_CREDENTIALS) ? LOGIN_FAILED_MESSAGE : failureCopy(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuthLayout title="Log in" idleNotice buildVersion>
      <form className="flex flex-col gap-5" onSubmit={(e) => void onSubmit(e)} noValidate>
        {accountClosed && (
          <Alert variant="info">
            <AlertDescription>
              <p>{copyText('deletionSignedOutNotice')}</p>
            </AlertDescription>
          </Alert>
        )}
        {expired && (
          <Alert variant="info">
            <AlertDescription>
              <p>{copyText('sessionExpired')}</p>
            </AlertDescription>
          </Alert>
        )}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={emailId}>Email</Label>
          <Input
            id={emailId}
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          {/* Siblings, not nested: the field's accessible name stays "Password" and the link keeps its own hit area. */}
          <div className="flex items-baseline justify-between gap-4">
            <Label htmlFor={passwordId}>Password</Label>
            <Link to="/reset" className="text-body-sm text-[var(--color-link)] hover:underline">
              Forgot your password?
            </Link>
          </div>
          <Input
            id={passwordId}
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        {error && (
          <Alert variant="blocked">
            <AlertDescription>
              <p>{error}</p>
            </AlertDescription>
          </Alert>
        )}

        <div className="flex flex-col gap-2">
          <Button
            type="submit"
            size="lg"
            disabled={!canSubmit}
            aria-busy={submitting || undefined}
            aria-describedby={reason ? reasonId : undefined}
          >
            Log in
          </Button>
          {reason && <DisabledReason id={reasonId}>{reason}</DisabledReason>}
        </div>

        <p className="text-body-sm text-navy-600">
          New to Trellis?{' '}
          <Link to="/signup" className="text-[var(--color-link)] hover:underline">
            Create an account
          </Link>
        </p>
      </form>
    </AuthLayout>
  )
}
